import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  alpha,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getNetDisposalError,
  isNetDisposalEnabled,
} from "./utils/s19dNetDisposal";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { TextFieldForAmount } from "../../../utils/TextField/NumericTextField";
import LimitedTextField from "../../../utils/TextField/LimitedTextField";
import { LIMITATION_TYPES } from "../../../../utils/static/limitation";
import { YearPicker } from "../../../utils/Form/YearPicker";
import { JustDropDown } from "../../../utils/Form/Dropdown";
import {
  getCaByType,
  getCaSettingByKey,
} from "../../../../builders/settings/CaSetting";
import { columnSettings, headerSettings } from "./utils/tableSettings";
import DecimalTextField from "../../../utils/TextField/DecimalTextField";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import {
  TextFieldForViewOnly,
  YearsClaim,
} from "../../../utils/TextField/TextField";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoIcon from "@mui/icons-material/Info";
import { StyledIconButton } from "../../../utils/Icon";
import { CustomDialogBase } from "../../../utils/dialog/customDialog";
import HpDetails from "./HpDetails";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  FA_REGISTER_CURRENT_YEAR_SOURCE,
  FA_REGISTER_PRIOR_YEAR_SOURCE,
} from "../../../../utils/static/CASchedule";

const isFARegisterCARow = (row) =>
  row?.source?.type === FA_REGISTER_PRIOR_YEAR_SOURCE ||
  row?.source?.type === FA_REGISTER_CURRENT_YEAR_SOURCE ||
  row?.source?.source === "FA Register" ||
  row?.source === "FA Register" ||
  row?.faRegisterSource ||
  row?.isImportedFromFARegister;

const CaTable = ({
  yearOfAssessment,
  caType,
  caSettings,
  dataType,
  dataRows,
  setDataRows,
  dataTotalRows,
  removeRow,
  onChange,
  fullscreen = false,
  isHirePurchase = false,
  canEdit = true,
}) => {
  const isReadMode = dataType === "Additions";

  const [sortOrder, setSortOrder] = useState("asc");
  const [viewPpDetails, setViewPpDetails] = useState(false);
  const [viewPpDetailAssets, setViewPpDetailAssets] = useState([]);

  const handleSort = () => {
    const isAscending = sortOrder === "asc";
    const sorted = [...dataRows].sort((a, b) =>
      isAscending ? a.ya - b.ya : b.ya - a.ya
    );
    setDataRows(sorted);
    setSortOrder(isAscending ? "desc" : "asc");
  };

  const renderSourceIndicator = (row) => {
    const isFA = isFARegisterCARow(row);
    const isManual = isHirePurchase && row?.manualData;

    if (!isFA && !isManual) return null;

    return (
      <Tooltip
        title={isFA ? "Imported from FA Register" : "Manually added by user"}
      >
        <Chip
          label={isFA ? "FA" : "M"}
          size='small'
          variant='filled'
          sx={(theme) => ({
            height: 16,
            fontSize: 9,
            fontWeight: 600,
            borderRadius: "3px",
            color: isFA ? theme.palette.info.main : theme.palette.warning.main,
            bgcolor: isFA
              ? theme.palette.action.hover
              : theme.palette.warning.main + "15",
            ".MuiChip-label": {
              px: 0.5,
            },
          })}
        />
      </Tooltip>
    );
  };

  const renderYearCell = (details, index) => {
    const isRowReadOnly =
      isReadMode ||
      (isHirePurchase && !details.manualData) ||
      isFARegisterCARow(details);
    const yearContent = isRowReadOnly ? (
      details.ya
    ) : (
      <YearPicker
        year={details.ya}
        onChange={onChange}
        reference={{
          index: index,
          field: "ya",
        }}
        customMaxDate={new Date(new Date().setFullYear(yearOfAssessment))}
        canEdit={canEdit}
      />
    );

    return (
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        spacing={0.5}
        sx={{
          minWidth: 90,
          width: "100%",
        }}
      >
        {yearContent}
        {renderSourceIndicator(details)}
      </Stack>
    );
  };

  const getTableCellContent = (
    header,
    data,
    index,
    isReadMode,
    isChildMode = false
  ) => {
    const { key } = header;
    if (key === "netDisposalConsiderationEnabled") {
      const enabled = Boolean(isNetDisposalEnabled(data));
      return (
        <Tooltip
          title={`${enabled ? "Disable" : "Enable"} WDA adjustment for partial disposal`}
        >
          <span>
            <Checkbox
              size='small'
              checked={enabled}
              disabled={!canEdit || !onChange}
              inputProps={{
                "aria-label": `WDA adjustment for partial disposal, row ${index + 1}`,
              }}
              onChange={(_event, checked) =>
                onChange(checked, {
                  index,
                  field: "netDisposalConsiderationEnabled",
                })
              }
            />
          </span>
        </Tooltip>
      );
    }
    const { type } = columnSettings[key];

    if (
      caType === "S19D" &&
      dataType === "Disposal" &&
      header.value === "Prior Year Disposal" &&
      isNetDisposalEnabled(data)
    ) {
      const error = getNetDisposalError(data);
      return (
        <Tooltip
          title={error}
          placement='top'
          arrow
          componentsProps={{
            tooltip: { sx: { maxWidth: 280, whiteSpace: "normal" } },
          }}
        >
          <Typography
            variant='body2'
            aria-label={`Prior Year Disposal, row ${index + 1}`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
              fontSize: "inherit",
            }}
          >
            {data.netDisposalConsideration == null
              ? "—"
              : formatAmount(-data.netDisposalConsideration)}
            {error && (
              <ErrorOutlineIcon
                color='error'
                fontSize='small'
                titleAccess={error}
              />
            )}
          </Typography>
        </Tooltip>
      );
    }

    const hasEnhanced =
      getCaSettingByKey(caSettings, data.caName) &&
      data["allowanceType"] === "Enhanced" &&
      getCaSettingByKey(caSettings, data.caName).enhancedAllowance;

    const renderHpAssetDetails = (data) => {
      return (
        <StyledIconButton
          onClick={() => {
            setViewPpDetails(true);
            setViewPpDetailAssets(data);
          }}
          icon={InfoIcon}
          tooltip='View Details'
          iconColor='info.main'
          sx={{
            "&:hover": {
              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.1),
            },
          }}
        />
      );
    };
    const renderAllowanceType = (data, caSettings, onChange, index, key) => {
      let scheduleDetails = getCaSettingByKey(caSettings, data["caName"]);
      if (
        !scheduleDetails ||
        !scheduleDetails.enhancedAllowance ||
        (scheduleDetails.enhancedAllowance && scheduleDetails.isOnlyEnhanced)
      )
        return data[key];

      return (
        <JustDropDown
          value={data[key]}
          options={["Base", "Enhanced"]}
          onChange={(e) => onChange(e.target.value, { index, field: key })}
          size='small'
          canEdit={canEdit}
        />
      );
    };

    const renderIaRate = (data, caSettings, onChange, index, key) => {
      return (
        <LimitedTextField
          value={data[key] || 0}
          onChange={onChange}
          limitationType={LIMITATION_TYPES.ONLY_NUMBERS}
          maxValue={100}
          warningMessage='Value is limited to 100'
          reference={{ index, field: key, isChildMode }}
          disabled={data.ya !== yearOfAssessment}
          canEdit={canEdit}
        />
      );
    };

    const renderS16Type = (data, caSettings, onChange, index, key) => {
      if (caType === "S16")
        return (
          <JustDropDown
            value={data[key]}
            options={["S16 - LIA", "S16 - IBA"]}
            onChange={(e) => onChange(e.target.value, { index, field: key })}
            disabled={!data["caName"]}
            size='small'
            canEdit={canEdit}
          />
        );
      else return <></>;
    };

    const renderNumericColumn = (
      value,
      enhancedValue,
      checkDeferred = false,
      key = ""
    ) => {
      if (
        isHirePurchase &&
        !data.manualData &&
        data.caName === "S19" &&
        key === "initialAllowance"
      ) {
        checkDeferred = false;
      }

      if (
        key === "annualAllowance" &&
        caType === "S19D" &&
        data.assetInUseAtFYE === false
      )
        return "N.A";

      return (
        <>
          {checkDeferred && data.isDeferred
            ? "Deferred"
            : `${hasEnhanced && key === "twdvBf" ? "Base: " : ""}${formatAmount(
                value
              )}`}

          {hasEnhanced && (!checkDeferred || !data.isDeferred) && (
            <>
              <Divider sx={{ mt: 1, mb: 1 }} />
              {["currentAmount", "currentYearDisposal"].includes(key) &&
              data.caName === "S19B YA 2024 - 28" ? (
                <TextFieldForAmount
                  value={data.enhanced[key] || 0}
                  onChange={onChange}
                  reference={{ index, field: key, allowanceType: "Enhanced" }}
                  label='Enhanced'
                  canEdit={canEdit}
                />
              ) : (
                <>{`${key === "twdvBf" ? "Enhanced: " : ""}${formatAmount(
                  enhancedValue
                )}`}</>
              )}
            </>
          )}
        </>
      );
    };

    const renderCheckBox = (data, caSettings, onChange, index, key) => {
      return canEdit ? (
        <Checkbox
          checked={data[key]}
          onChange={(e) =>
            onChange(e.target.checked, { index, field: key, isChildMode })
          }
        />
      ) : (
        <TextFieldForViewOnly value={data[key] ? "Yes" : "No"} />
      );
    };

    const renderDisregardRate = (data, caSettings, onChange, index, key) => {
      return (
        <DecimalTextField
          value={data[key]}
          onChange={onChange}
          decimalPlaces={4}
          maxValue={100}
          warningMessage='Value is limited 100 and 4 decimal places'
          reference={{ index, field: key, isChildMode }}
          disabled={!data["isDisregard"]}
          canEdit={canEdit}
        />
      );
    };

    if (isReadMode) {
      // READ MODE: Display values only
      switch (type) {
        case "numeric":
          return [
            "initialAllowance",
            "annualAllowance",
            "twdvCf",
            "currentAmount",
            "totalAmount",
            "currentYearDisposal",
            "twdvBf",
            "cost",
          ].includes(key) ? (
            renderNumericColumn(
              data[key],
              data.enhanced[key],
              ["annualAllowance", "initialAllowance"].includes(key),
              key
            )
          ) : ["principalPaid"].includes(key) ? (
            <>
              {formatAmount(data[key])} {renderHpAssetDetails(data["hpAssets"])}
            </>
          ) : (
            formatAmount(data[key])
          );
        case "currentAmount":
          return renderNumericColumn(data[key], data.enhanced[key], false);
        case "bool":
          return data[key] ? "Yes" : "No";
        case "assetInUseAtFYE":
          return renderCheckBox(data, caSettings, onChange, index, key);
        case "allowanceType":
          return renderAllowanceType(data, caSettings, onChange, index, key);
        case "noOfYearClaims":
          return data[key];
        case "caName":
          if (
            caType === "S16" &&
            ["S16 - LIA", "S16 - IBA"].includes(data["caName"])
          )
            return "S16 - (LIA / IBA)";
          return data[key];
        case "caSummary":
          if (caType === "S16") return data["caName"];
          return data[key];
        case "initialAllowanceRate":
          return renderIaRate(data, caSettings, onChange, index, key);
        case "isDisregard":
          return renderCheckBox(data, caSettings, onChange, index, key);
        case "disregardRate":
          return renderDisregardRate(data, caSettings, onChange, index, key);
        case "principalPaid":
          return (
            <>
              {formatAmount(data[key])} {renderHpAssetDetails(data["hpAssets"])}
            </>
          );
        default:
          return data[key];
      }
    } else {
      // EDIT MODE: Render input components
      switch (key) {
        case "caName":
          if (isHirePurchase && !data.manualData) {
            if (
              caType === "S16" &&
              ["S16 - LIA", "S16 - IBA"].includes(data["caName"])
            )
              return "S16 - (LIA / IBA)";
            else return data[key];
          } else
            return (
              <JustDropDown
                value={data[key]}
                options={getCaByType(caSettings, caType)}
                onChange={(e) =>
                  onChange(e.target.value, { index, field: key })
                }
                size='small'
                canEdit={canEdit}
              />
            );
        case "caSummary":
          if (caType === "S16" && !isHirePurchase)
            return renderS16Type(data, caSettings, onChange, index, "s16Type");
          else if (isHirePurchase && caType === "S16") return data["caName"];
          return data[key];
        case "allowanceType":
          return renderAllowanceType(data, caSettings, onChange, index, key);
        case "noOfYearClaims":
          return !(isHirePurchase && !data.manualData) &&
            (data.caName === "" ||
              getCaSettingByKey(caSettings, data.caName)?.numberOfYears ===
                null) ? (
            <YearsClaim
              value={data[key]}
              handleInputChange={onChange}
              reference={{ index, field: key }}
              numberOfYears={null}
              canEdit={canEdit}
            />
          ) : (
            data[key]
          );
        case "noOfYearClaimsBf":
          if (isHirePurchase && !data.manualData) return data[key];
          else
            return (
              <YearsClaim
                value={data[key]}
                handleInputChange={onChange}
                reference={{ index, field: key }}
                numberOfYears={
                  data.caName
                    ? (getCaSettingByKey(caSettings, data.caName)
                        ?.numberOfYears ?? data["noOfYearClaims"])
                    : null
                }
                canEdit={canEdit}
              />
            );
        case "initialAllowanceRate":
          return renderIaRate(data, caSettings, onChange, index, key);
        case "cost":
          if (isFARegisterCARow(data) || (isHirePurchase && !data.manualData))
            return formatAmount(data[key]);
          return (
            <TextFieldForAmount
              value={data[key] ?? 0}
              onChange={onChange}
              reference={{ index, field: key, isChildMode }}
              canEdit={canEdit}
            />
          );
        case "disregardRate":
          return renderDisregardRate(data, caSettings, onChange, index, key);
        case "twdvBf":
          return (
            <>
              {canEdit ? (
                <Stack direction='row' alignItems='center' spacing={1}>
                  {isHirePurchase && data["caName"] !== "S19" && (
                    <StyledIconButton
                      icon={RefreshIcon}
                      onClick={() => {
                        onChange(data["ori_twdvBf"], {
                          index,
                          field: key,
                          allowanceType: "Base",
                        });
                      }}
                      tooltip='Recalculate TWDV B/F'
                      iconColor='primary.main'
                    />
                  )}
                  <TextFieldForAmount
                    value={data[key]}
                    onChange={onChange}
                    reference={{ index, field: key, allowanceType: "Base" }}
                    label={hasEnhanced ? "Base" : ""}
                    canEdit={canEdit}
                  />
                </Stack>
              ) : (
                <TextFieldForViewOnly value={formatAmount(data[key])} />
              )}

              {hasEnhanced && (
                <>
                  <Divider sx={{ mt: 1, mb: 1 }} />
                  <TextFieldForAmount
                    value={data.enhanced[key] || 0}
                    onChange={onChange}
                    reference={{ index, field: key, allowanceType: "Enhanced" }}
                    label='Enhanced'
                    canEdit={canEdit}
                  />
                </>
              )}
            </>
          );
        case "isDeferred":
        case "isDisregard":
        case "assetInUseAtFYE":
          return renderCheckBox(data, caSettings, onChange, index, key);
        case "annualAllowance":
        case "initialAllowance":
          return renderNumericColumn(data[key], data.enhanced[key], true, key);
        case "principalPaid":
          if (data.manualData)
            return (
              <TextFieldForAmount
                value={data[key]}
                onChange={onChange}
                reference={{ index, field: key, allowanceType: "Base" }}
                label={hasEnhanced ? "Base" : ""}
                canEdit={canEdit}
              />
            );
          else
            return (
              <>
                {formatAmount(data[key])}{" "}
                {renderHpAssetDetails(data["hpAssets"])}
              </>
            );
        case "twdvCf":
        case "currentAmount":
        case "totalAmount":
          return renderNumericColumn(data[key], data.enhanced[key], false, key);
        default:
          return data[key];
      }
    }
  };

  const isHeaderVisible = (header) => {
    if (!header.visibility.includes(caType)) return false;
    if (dataType === "Disposal" && header.showInDisposal === false)
      return false;
    if (isHirePurchase) {
      if (header.showInHirePurchase === false) return false;
      return true;
    }
    if (header.showInHirePurchase === true) return false;
    return true;
  };

  const currentHeaderSettings = headerSettings.filter((item) => {
    const baseCondition = isReadMode
      ? item.value !== "Prior Year Disposal"
      : item.value !== "Additions" && item.value !== "Current Year Disposal";

    if (["Deferred"].includes(item.value)) {
      return !isHirePurchase;
    }

    if (item.value === "Principal Paid") {
      return isHirePurchase;
    }

    return baseCondition;
  });

  const showWdaAdjustment =
    caType === "S19D" && dataType === "Disposal" && !isHirePurchase;
  const visibleHeaders = currentHeaderSettings
    .filter(isHeaderVisible)
    .flatMap((header) =>
      showWdaAdjustment && header.value === "Prior Year Disposal"
        ? [
            {
              key: "netDisposalConsiderationEnabled",
              value: "WDA adj.",
              align: "center",
            },
            header,
          ]
        : [header]
    );
  const canRemoveRow = (row) =>
    !(isHirePurchase && !row.manualData) && !isFARegisterCARow(row);
  const showActions = Boolean(
    canEdit && dataRows?.length && removeRow && dataRows.some(canRemoveRow)
  );
  const actionCellSx = {
    position: "sticky",
    right: 0,
    zIndex: 1,
    minWidth: 64,
    width: 64,
    bgcolor: "background.paper",
    borderLeft: "1px solid",
    borderLeftColor: "divider",
    boxShadow: (theme) =>
      `-3px 0 6px -3px ${alpha(theme.palette.common.black, 0.15)}`,
  };

  // Keep amounts readable and let descriptions wrap as the table grows.
  const getColumnSx = (header) => {
    const { key } = header;
    if (key === "netDisposalConsiderationEnabled")
      return { minWidth: 80, width: 80 };
    const isDescription = ["caName", "caSummary"].includes(key);
    const isAmount = columnSettings[key].align === "right";
    const preferredWidths = { caName: 190, caSummary: 170, allowanceType: 116 };
    const hasRecalculateButton =
      key === "twdvBf" && isHirePurchase && canEdit && !isReadMode;
    const minWidth = hasRecalculateButton
      ? 180
      : preferredWidths[key] || (isAmount ? 136 : 88);

    return {
      minWidth,
      width: minWidth,
      ...(isDescription && { maxWidth: 280, overflowWrap: "anywhere" }),
      whiteSpace: isAmount ? "nowrap" : "normal",
      "& .MuiFormControl-root": { width: "100%", minWidth: 0 },
      "& .MuiInputBase-input": { minWidth: 0 },
      "& .MuiSelect-select": { whiteSpace: "normal", overflowWrap: "anywhere" },
      "& .MuiIconButton-root": { flexShrink: 0 },
    };
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      role='region'
      aria-label={`${caType} ${isReadMode ? "Current YA" : "Prior YA"}${isHirePurchase ? " hire purchase" : ""} table`}
      tabIndex={0}
      sx={{
        border: "none",
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        maxHeight: fullscreen ? "calc(100vh - 100px)" : 500,
        overflow: "auto",
        scrollbarWidth: "auto",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
      }}
    >
      <Table
        stickyHeader
        size='small'
        sx={{
          width: "100%",
          tableLayout: "auto",
          "& .MuiTableCell-root": {
            px: 1.5,
            py: 1.25,
            fontSize: "0.8125rem",
          },
          "& .MuiTableCell-head": {
            py: 1.5,
            fontWeight: 600,
            lineHeight: 1.4,
            backgroundColor: (theme) => theme.palette.background.paper,
            borderBottom: "2px solid",
            borderBottomColor: "divider",
          },
          "& .MuiTableCell-body .MuiInputBase-root": {
            fontSize: "inherit",
            // Keep editable fields distinct from row hover and child-row tints.
            "&:not(.Mui-disabled)": { backgroundColor: "common.white" },
          },
          "& .MuiTableCell-body .MuiSelect-select": { fontSize: "inherit" },
          "& .MuiTableCell-body.MuiTableCell-alignRight": {
            whiteSpace: "nowrap",
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            }}
          >
            {isReadMode ? (
              <TableCell sx={{ minWidth: 80 }} align='center'>
                YA
              </TableCell>
            ) : (
              <TableCell align='center'>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minWidth: 90,
                  }}
                >
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={handleSort}
                  >
                    YA
                  </TableSortLabel>
                </Box>
              </TableCell>
            )}
            {visibleHeaders.map((header) => (
              <TableCell
                key={header.value}
                align={header.align}
                sx={{ ...getColumnSx(header), whiteSpace: "normal" }}
              >
                {header.key === "netDisposalConsiderationEnabled" ? (
                  <Tooltip title='WDA adjustment for partial disposal'>
                    <span>{header.value}</span>
                  </Tooltip>
                ) : (
                  header.value
                )}
              </TableCell>
            ))}
            {showActions && (
              <TableCell align='center' sx={{ ...actionCellSx, zIndex: 3 }}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!dataRows || dataRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleHeaders.length + 1 + (showActions ? 1 : 0)}
                align='center'
                style={{ height: 80, color: "#888" }}
              >
                No data available
              </TableCell>
            </TableRow>
          ) : (
            dataRows.map((details, index) => (
              <React.Fragment key={index}>
                <TableRow
                  hover
                  sx={{
                    ...(caType === "S16" &&
                      dataType === "Additions" &&
                      details.isSeparate &&
                      Object.keys(details.separate).length > 0 && {
                        backgroundColor: "rgba(214, 204, 235, 0.5)",
                      }),
                    minHeight: 64,
                  }}
                >
                  <TableCell align='center'>
                    {renderYearCell(details, index)}
                  </TableCell>

                  {visibleHeaders.map((header) => {
                    return (
                      <TableCell
                        key={header.value}
                        align={
                          columnSettings[header.key]?.align || header.align
                        }
                        sx={getColumnSx(header)}
                      >
                        {getTableCellContent(
                          header,
                          details,
                          index,
                          isReadMode || isFARegisterCARow(details)
                        )}
                      </TableCell>
                    );
                  })}

                  {showActions && (
                    <TableCell align='center' sx={actionCellSx}>
                      <Stack
                        direction='row'
                        justifyContent='center'
                        spacing={0.5}
                      >
                        {removeRow && canRemoveRow(details) && (
                          <IconButtonWithTooltip
                            icon={DeleteOutlineIcon}
                            tooltipTitle='Delete Row'
                            onClick={() => removeRow(index, isHirePurchase)}
                            color='error'
                            size='small'
                          />
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
                {caType === "S16" &&
                  dataType === "Additions" &&
                  details.isSeparate &&
                  Object.keys(details.separate).length > 0 && (
                    <TableRow
                      key={`${index}-child`}
                      sx={{
                        backgroundColor: "rgba(237, 231, 246, 0.3)",
                        "& td": { borderTop: "none" },
                        "&:hover": {
                          backgroundColor: "rgba(237, 231, 246, 0.5)",
                        },
                        transition: "background-color 0.2s ease-in-out",
                      }}
                    >
                      <TableCell align='center'>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap", // Prevent wrapping
                            gap: 0.5, // Small gap between icon and text
                          }}
                        >
                          <SubdirectoryArrowRightIcon
                            sx={{ fontSize: 14, color: "primary.main" }}
                          />
                          {details.ya}
                        </Box>
                      </TableCell>

                      {visibleHeaders.map((header) => {
                        return (
                          <TableCell
                            key={header.value}
                            align={columnSettings[header.key].align}
                            sx={getColumnSx(header)}
                          >
                            {getTableCellContent(
                              header,
                              details.separate,
                              index,
                              isReadMode,
                              true
                            )}
                          </TableCell>
                        );
                      })}
                      {showActions && <TableCell sx={actionCellSx} />}
                    </TableRow>
                  )}
              </React.Fragment>
            ))
          )}
          <TableRow
            sx={{
              position: "sticky",
              bottom: 0,
              background: (theme) => theme.palette.background.paper,
              zIndex: 2,
              borderTop: (theme) => `3px solid ${theme.palette.primary.main}`,
              "& .MuiTableCell-root": { fontWeight: 700 },
              boxShadow: (theme) =>
                `0 -2px 8px 0 ${theme.palette.action.selected}`,
              transition: "background 0.2s",
            }}
          >
            <TableCell
              sx={{ background: "inherit", zIndex: 2, fontWeight: 700 }}
            ></TableCell>

            {dataTotalRows &&
              visibleHeaders.map((header) => {
                if (
                  [
                    "twdvBf",
                    "cost",
                    "currentAmount",
                    "totalAmount",
                    "initialAllowance",
                    "annualAllowance",
                    "twdvCf",
                    "currentYearDisposal",
                    "principalPaid",
                  ].includes(header.key)
                ) {
                  return (
                    <TableCell
                      key={header.value}
                      align='right'
                      sx={{
                        background: (theme) => theme.palette.background.paper,
                        zIndex: 2,
                      }}
                    >
                      {formatAmount(dataTotalRows[header.key])}
                    </TableCell>
                  );
                } else if (header.key === "noOfYearClaimsBf") {
                  return (
                    <TableCell
                      key={header.value}
                      align='right'
                      sx={{
                        background: "inherit",
                        zIndex: 2,
                        fontWeight: 700,
                      }}
                    >
                      Sub total
                    </TableCell>
                  );
                } else {
                  return (
                    <TableCell
                      key={header.value}
                      sx={{
                        background: "inherit",
                        zIndex: 2,
                        fontWeight: 700,
                      }}
                    ></TableCell>
                  );
                }
              })}

            {showActions && <TableCell sx={{ ...actionCellSx, zIndex: 3 }} />}
          </TableRow>
          {caType === "S16" && (
            <>
              <TableRow>
                <TableCell></TableCell>

                {visibleHeaders.map((header) => {
                  if (
                    ["initialAllowance", "annualAllowance"].includes(header.key)
                  ) {
                    return (
                      <TableCell key={header.value} align='right'>
                        {formatAmount(
                          Math.abs(dataTotalRows.disregard[header.key])
                        )}
                      </TableCell>
                    );
                  } else if (header.key === "disregardRate") {
                    return (
                      <TableCell
                        key={header.value}
                        align='right'
                        sx={{ fontWeight: "bold" }}
                      >
                        Disregarded
                      </TableCell>
                    );
                  } else {
                    return <TableCell key={header.value} />;
                  }
                })}

                {showActions && <TableCell sx={actionCellSx} />}
              </TableRow>
              <TableRow>
                <TableCell></TableCell>

                {visibleHeaders.map((header) => {
                  if (
                    ["initialAllowance", "annualAllowance"].includes(header.key)
                  ) {
                    return (
                      <TableCell key={header.value} align='right'>
                        {formatAmount(
                          dataTotalRows[header.key] -
                            dataTotalRows.disregard[header.key]
                        )}
                      </TableCell>
                    );
                  } else if (header.key === "disregardRate") {
                    return (
                      <TableCell
                        key={header.value}
                        align='right'
                        sx={{ fontWeight: "bold" }}
                      >
                        Total
                      </TableCell>
                    );
                  } else {
                    return <TableCell key={header.value} />;
                  }
                })}

                {showActions && <TableCell sx={actionCellSx} />}
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
      <CustomDialogBase
        open={viewPpDetails}
        onClose={() => {
          setViewPpDetailAssets([]);
          setViewPpDetails(false);
        }}
        title='Hire Purchase Allowance Details'
        type='info'
      >
        <HpDetails assets={viewPpDetailAssets} />
      </CustomDialogBase>
    </TableContainer>
  );
};

export default CaTable;
